import React, { useCallback, useEffect, useState } from 'react';

import { Group, Modal, Text, useMantineTheme } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconEdit, IconTrashX } from '@tabler/icons-react';
import Menu from 'components/Menu';
import Table from 'components/Table';
import usePackageCategories from 'hooks/packageCategory/usePackageCategories';
import { CellProps } from 'react-table';
import { PackageCategory } from 'types';

import DeletePackageCategoryModal from './DeletePackageCategoryModal';
import DrawerPackageCategoryForm from './DrawerPackageCategoryForm';

interface IPackageCategoryTableContainerProps {
  searchTerm: string;
  permission?: boolean;
}

const PackageCategoryTableContainer = ({
  searchTerm,
  permission = true,
}: IPackageCategoryTableContainerProps): JSX.Element => {
  const theme = useMantineTheme();
  const [tableData, setTableData] = useState<PackageCategory[]>([]);
  const [ordering, setOrdering] = useState('name');
  const [ascending, setAscending] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState<PackageCategory | undefined>();
  const [isDeleteModalOpen, { open: openDeleteModal, close: closeDeleteModal }] = useDisclosure(false);
  const [isEditFormOpen, { open: openEditForm, close: closeEditForm }] = useDisclosure(false);
  const { data, fetchNextPage, hasNextPage, isSuccess, isError, isLoading, isFetchingNextPage } =
    usePackageCategories({
      search: searchTerm,
      ordering: ascending ? ordering : `-${ordering}`,
    });

  const handleEdit = (category: PackageCategory): void => {
    setSelectedCategory(category);
    openEditForm();
  };

  const handleHeaderClick = useCallback(
    (header: string): void => {
      setOrdering(header);
      setAscending(!ascending);
    },
    [ascending],
  );

  const handleCloseEditForm = (): void => {
    closeEditForm();
    setSelectedCategory(undefined);
  };

  const handleDelete = (category: PackageCategory): void => {
    setSelectedCategory(category);
    openDeleteModal();
  };

  const columns = [
    {
      Header: 'Package Category',
      accessor: 'name',
      Cell: ({ row }: CellProps<PackageCategory>) => (
        <Group>
          <Text
            onClick={() => handleEdit(row.original)}
            weight='bold'
            underline
            sx={{ width: 'fit-content', cursor: 'pointer' }}
          >
            {row.original.name}
          </Text>
        </Group>
      ),
    },
    {
      id: 'action',
      disableSortBy: true,
      Cell: ({ row }: CellProps<PackageCategory>) => {
        const items = [
          {
            title: 'Edit Category',
            icon: <IconEdit size={theme.fontSizes.sm} />,
            onClick: () => handleEdit(row.original),
          },
          {
            title: 'Delete Category',
            icon: <IconTrashX size={theme.fontSizes.sm} />,
            color: 'red',
            onClick: () => handleDelete(row.original),
          },
        ];
        return (
          <Group position='right' px='xs'>
            <Menu position='bottom-end' items={items} disabled={!permission} />
          </Group>
        );
      },
    },
  ];

  useEffect(() => {
    if (!isLoading && data && isSuccess) {
      setTableData(data.pages.map(page => page.data.results).flat());
    }
  }, [isLoading, data, isSuccess]);

  return (
    <>
      <Table
        tableData={tableData}
        columns={columns}
        isSuccess={isSuccess}
        isLoading={isLoading}
        isError={isError}
        isFetchingNextPage={isFetchingNextPage}
        hasNextPage={!!hasNextPage}
        fetchNextPage={fetchNextPage}
        clickableHeaders={['name']}
        handleHeaderClick={handleHeaderClick}
      />
      <Modal
        opened={isDeleteModalOpen}
        onClose={closeDeleteModal}
        title={
          <Text size='lg' weight='bold'>
            Delete Package Category
          </Text>
        }
      >
        {selectedCategory && (
          <DeletePackageCategoryModal packageCategory={selectedCategory} handleClose={closeDeleteModal} />
        )}
      </Modal>
      <DrawerPackageCategoryForm
        formId='edit-package-category-form'
        title={
          <Text size='lg' weight='bold'>
            Edit Package Category
          </Text>
        }
        opened={isEditFormOpen}
        closeHandler={handleCloseEditForm}
        permission={permission}
        type='edit'
        packageCategoryId={selectedCategory?.packageCategoryId}
      />
    </>
  );
};

export default PackageCategoryTableContainer;
